// src/pages/Login.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";

import { auth, provider, signInWithPopup } from "../firebase/firebaseConfig";
import socket from "../socket";

import "./login.css";
import "./demoSchoolLogin.css";

const ROLE_ORDER = [
  "superadmin",
  "admin",
  "principal",
  "accounts",
  "hr",
  "academic_coordinator",
  "coordinator",
  "teacher",
  "student",
];

const Login = () => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [school, setSchool] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);

  const navigate = useNavigate();
  const userInputRef = useRef(null);

  const apiBase = useMemo(
    () => process.env.REACT_APP_API_URL?.replace(/\/+$/, ""),
    []
  );

  const backgroundImage = `${process.env.PUBLIC_URL}/images/demoSchool.png`;

  // =========================================================
  // Focus username on load
  // =========================================================
  useEffect(() => {
    userInputRef.current?.focus();
  }, []);

  // =========================================================
  // Fetch school information
  // =========================================================
  useEffect(() => {
    if (!apiBase) return;

    axios
      .get(`${apiBase}/schools`)
      .then((res) => {
        if (res.data?.length) {
          setSchool(res.data[0]);
        }
      })
      .catch((err) => {
        console.warn(
          "Unable to fetch school information:",
          err?.message
        );
      });
  }, [apiBase]);

  // =========================================================
  // Restore saved token
  // =========================================================
  useEffect(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    if (token) {
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
    }
  }, []);

  // =========================================================
  // Global 401 handling
  // =========================================================
  useEffect(() => {
    const interceptor =
      axios.interceptors.response.use(
        (response) => response,
        (err) => {
          const status = err?.response?.status;

          if (status === 401) {
            delete axios.defaults.headers.common[
              "Authorization"
            ];

            localStorage.removeItem("token");
            localStorage.removeItem("roles");
            localStorage.removeItem("username");
            localStorage.removeItem("userId");
            localStorage.removeItem("name");
            localStorage.removeItem("activeRole");
            localStorage.removeItem("family");
            localStorage.removeItem(
              "activeStudentAdmission"
            );

            sessionStorage.removeItem("token");
            sessionStorage.removeItem("roles");
            sessionStorage.removeItem("username");
            sessionStorage.removeItem("userId");
            sessionStorage.removeItem("name");

            try {
              socket.disconnect();
            } catch (socketError) {
              console.warn(
                "Socket disconnect failed:",
                socketError
              );
            }

            window.dispatchEvent(
              new Event("user-logged-out")
            );

            navigate("/login", {
              replace: true,
            });
          }

          return Promise.reject(err);
        }
      );

    return () =>
      axios.interceptors.response.eject(
        interceptor
      );
  }, [navigate]);

  // =========================================================
  // Socket room handling
  // =========================================================
  const joinRooms = (user, roles = []) => {
    const normalizedRoles = roles.map((role) =>
      (role || "").toLowerCase()
    );

    if (normalizedRoles.includes("student")) {
      socket.emit("joinRoom", {
        room: user.username,
      });

      socket.emit("joinRoom", {
        room: "students",
      });
    }

    if (
      normalizedRoles.includes("teacher") ||
      normalizedRoles.includes(
        "academic_coordinator"
      ) ||
      normalizedRoles.includes("coordinator")
    ) {
      socket.emit("joinRoom", {
        room: `teacher-${user.id}`,
      });

      socket.emit("joinRoom", {
        room: "teachers",
      });
    }

    if (
      normalizedRoles.includes("admin") ||
      normalizedRoles.includes(
        "superadmin"
      ) ||
      normalizedRoles.includes("principal")
    ) {
      socket.emit("joinRoom", {
        room: "admins",
      });
    }

    if (normalizedRoles.includes("accounts")) {
      socket.emit("joinRoom", {
        room: "accounts",
      });
    }
  };

  // =========================================================
  // Common after-login handler
  // =========================================================
  const afterAuth = async (data) => {
    const { token, user, roles } = data;

    const roleArray = Array.isArray(roles)
      ? roles
      : roles
      ? [roles]
      : [];

    const roleArrayLower = roleArray.map((role) =>
      (role || "").toLowerCase()
    );

    // -------------------------------------------------------
    // Save login information
    // -------------------------------------------------------
    try {
      if (remember) {
        localStorage.setItem("token", token);

        localStorage.setItem(
          "roles",
          JSON.stringify(roleArray)
        );

        localStorage.setItem(
          "username",
          user?.username || ""
        );

        localStorage.setItem(
          "userId",
          user?.id || ""
        );

        localStorage.setItem(
          "name",
          user?.name || ""
        );
      } else {
        sessionStorage.setItem(
          "token",
          token
        );

        sessionStorage.setItem(
          "roles",
          JSON.stringify(roleArray)
        );

        sessionStorage.setItem(
          "username",
          user?.username || ""
        );

        sessionStorage.setItem(
          "userId",
          user?.id || ""
        );

        sessionStorage.setItem(
          "name",
          user?.name || ""
        );
      }

      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
    } catch (storageError) {
      console.warn(
        "Login storage failed:",
        storageError
      );
    }

    // -------------------------------------------------------
    // Student family / student switcher
    // -------------------------------------------------------
    try {
      if (data.family) {
        localStorage.setItem(
          "family",
          JSON.stringify(data.family)
        );

        localStorage.setItem(
          "activeStudentAdmission",
          data.family?.student
            ?.admission_number ||
            user?.username ||
            ""
        );
      } else {
        localStorage.removeItem("family");

        localStorage.removeItem(
          "activeStudentAdmission"
        );
      }

      window.dispatchEvent(
        new Event("family-updated")
      );
    } catch (familyError) {
      console.warn(
        "Failed to save family:",
        familyError
      );
    }

    // -------------------------------------------------------
    // Set default role
    // -------------------------------------------------------
    localStorage.removeItem("userRole");

    const defaultActiveRole =
      ROLE_ORDER.find((role) =>
        roleArrayLower.includes(role)
      ) ||
      roleArrayLower[0] ||
      "";

    localStorage.setItem(
      "activeRole",
      defaultActiveRole
    );

    // -------------------------------------------------------
    // Save FCM token if available
    // -------------------------------------------------------
    try {
      const fcmToken = window.FCMTOKEN;

      if (fcmToken) {
        axios
          .post(`${apiBase}/users/save-token`, {
            username: user.username,
            token: fcmToken,
          })
          .catch((fcmError) => {
            console.warn(
              "FCM token save failed:",
              fcmError?.response?.data ||
                fcmError?.message
            );
          });
      }
    } catch (fcmError) {
      console.warn(
        "FCM token error:",
        fcmError
      );
    }

    // -------------------------------------------------------
    // Reconnect authenticated socket
    // -------------------------------------------------------
    try {
      if (token) {
        socket.auth = { token };

        if (socket.connected) {
          socket.disconnect();
        }

        socket.connect();
      }
    } catch (socketError) {
      console.warn(
        "Socket authentication failed:",
        socketError
      );
    }

    // -------------------------------------------------------
    // Join socket rooms
    // -------------------------------------------------------
    try {
      joinRooms(user, roleArrayLower);
    } catch (roomError) {
      console.warn(
        "Socket room joining failed:",
        roomError
      );
    }

    window.dispatchEvent(
      new Event("role-changed")
    );

    // -------------------------------------------------------
    // Redirect
    // -------------------------------------------------------
    const redirectPath =
      defaultActiveRole === "accounts"
        ? "/accounts-dashboard"
        : "/dashboard";

    navigate(redirectPath, {
      replace: true,
    });
  };

  // =========================================================
  // Username / password login
  // =========================================================
  const handleLogin = async (event) => {
    event.preventDefault();

    setError("");
    setLoading(true);

    try {
      if (!apiBase) {
        throw new Error(
          "Application API is not configured."
        );
      }

      const device =
        navigator.userAgent || "web";

      const { data } = await axios.post(
        `${apiBase}/users/login`,
        {
          login: login.trim(),
          password,
          device,
        }
      );

      await afterAuth(data);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Invalid username or password.";

      setError(message);

      console.error(
        "Login error:",
        err
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Google login
  // =========================================================
  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      if (!apiBase) {
        throw new Error(
          "Application API is not configured."
        );
      }

      const result =
        await signInWithPopup(
          auth,
          provider
        );

      const googleUser = result.user;

      const device =
        navigator.userAgent || "web";

      const { data } = await axios.post(
        `${apiBase}/users/login`,
        {
          google_id: googleUser.uid,
          google_email: googleUser.email,
          google_name:
            googleUser.displayName,
          google_username:
            googleUser.email,
          device,
        }
      );

      await afterAuth(data);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Google login failed.";

      setError(message);

      console.error(
        "Google login error:",
        err
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Branding
  // =========================================================
  const fallbackLogo =
    `${process.env.PUBLIC_URL}/images/SmartoLogo.png`;

  const schoolLogoSrc =
    school?.logo && apiBase
      ? `${apiBase}${school.logo}`
      : fallbackLogo;

  const schoolName =
    school?.name || "Demo School";

  // =========================================================
  // Icons
  // =========================================================
  const UserIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="7"
        r="4"
      />

      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>
  );

  const LockIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect
        x="4"
        y="10"
        width="16"
        height="11"
        rx="2"
      />

      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );

  const EyeIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />

      <circle
        cx="12"
        cy="12"
        r="2.5"
      />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3 3 18 18" />

      <path d="M10.6 10.7a2 2 0 0 0 2.7 2.7" />

      <path d="M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 8 10 8a17 17 0 0 1-2.1 3.2" />

      <path d="M6.6 6.6C3.8 8.5 2 12 2 12s3.5 8 10 8a9.7 9.7 0 0 0 4.1-.9" />
    </svg>
  );

  const ArrowIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );

  const ShieldIcon = () => (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );

  const GoogleIcon = () => (
    <svg
      width="19"
      height="19"
      viewBox="0 0 533.5 544.3"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M533.5 278.4c0-17.4-1.6-34.1-4.7-50.2H272v95.1h147.1c-6.3 34-25 62.8-53.3 82v67h86.2c50.4-46.5 81.5-115 81.5-193.9z"
      />

      <path
        fill="#34A853"
        d="M272 544.3c72.3 0 132.9-23.9 177.2-65.1l-86.2-67c-24 16.1-54.6 25.7-91 25.7-69.9 0-129.1-47.2-150.3-110.7H33.7v69.6C77.8 490.3 168.8 544.3 272 544.3z"
      />

      <path
        fill="#FBBC04"
        d="M121.7 327.2a163.7 163.7 0 0 1 0-104.4v-69.6H33.7a272.3 272.3 0 0 0 0 243.6l88-69.6z"
      />

      <path
        fill="#EA4335"
        d="M272 107.7c39.2 0 74.5 13.5 102.2 39.9l76.7-76.7C404.8 26.2 344.2 0 272 0 168.8 0 77.8 54 33.7 153.2l88 69.6C142.9 154.9 202.1 107.7 272 107.7z"
      />
    </svg>
  );

  return (
    <main className="demo-school-login premium-login-page">

      {/* =====================================================
          LEFT SIDE
          PURE SCHOOL IMAGE ONLY
          No text / no logo / no overlay
      ===================================================== */}
      <section
        className="premium-login-visual"
        aria-label="School Campus"
        style={{
          backgroundImage: `url("${backgroundImage}")`,
        }}
      />

      {/* =====================================================
          RIGHT SIDE — PREMIUM LOGIN PANEL
      ===================================================== */}
      <section className="premium-login-panel">

        <div className="login-panel-inner">

          {/* SCHOOL BRAND */}
          <div className="login-school-brand">

            <div className="login-school-logo-wrap">
              <img
                src={schoolLogoSrc}
                alt={`${schoolName} Logo`}
                className="login-school-logo"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src =
                    fallbackLogo;
                }}
              />
            </div>

            <div className="login-school-brand-copy">
              <div className="login-school-name">
                {schoolName}
              </div>

              <div className="login-school-subtitle">
                School Management Portal
              </div>
            </div>

          </div>

          {/* HEADING */}
          <div className="login-heading">

            <div className="login-mini-label">
              SECURE PORTAL
            </div>

            <h1>
              Welcome back
            </h1>

            <p>
              Sign in to access your school dashboard
              and continue where you left off.
            </p>

          </div>

          {/* ERROR */}
          {error && (
            <div
              className="premium-alert"
              role="alert"
            >
              <div className="premium-alert-icon">
                !
              </div>

              <div className="premium-alert-message">
                {error}
              </div>
            </div>
          )}

          {/* LOGIN FORM */}
          <form
            onSubmit={handleLogin}
            className="premium-login-form"
            noValidate
          >

            {/* USERNAME */}
            <div className="premium-field">

              <label htmlFor="login">
                Username or Email
              </label>

              <div className="premium-input-wrap">

                <span className="premium-input-icon">
                  <UserIcon />
                </span>

                <input
                  ref={userInputRef}
                  id="login"
                  name="login"
                  type="text"
                  value={login}
                  onChange={(event) =>
                    setLogin(
                      event.target.value
                    )
                  }
                  placeholder="Enter username or email"
                  autoComplete="username"
                  required
                />

              </div>

            </div>

            {/* PASSWORD */}
            <div className="premium-field">

              <div className="premium-label-row">

                <label htmlFor="password">
                  Password
                </label>

                <button
                  type="button"
                  className="forgot-link"
                  onClick={() =>
                    navigate(
                      "/forgot-password"
                    )
                  }
                >
                  Forgot password?
                </button>

              </div>

              <div className="premium-input-wrap">

                <span className="premium-input-icon">
                  <LockIcon />
                </span>

                <input
                  id="password"
                  name="password"
                  type={
                    showPass
                      ? "text"
                      : "password"
                  }
                  value={password}
                  onChange={(event) =>
                    setPassword(
                      event.target.value
                    )
                  }
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />

                <button
                  type="button"
                  className="password-toggle"
                  aria-label={
                    showPass
                      ? "Hide password"
                      : "Show password"
                  }
                  title={
                    showPass
                      ? "Hide password"
                      : "Show password"
                  }
                  onClick={() =>
                    setShowPass(
                      (current) =>
                        !current
                    )
                  }
                >
                  {showPass ? (
                    <EyeOffIcon />
                  ) : (
                    <EyeIcon />
                  )}
                </button>

              </div>

            </div>

            {/* REMEMBER */}
            <div className="premium-login-options">

              <label className="premium-remember">

                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) =>
                    setRemember(
                      event.target.checked
                    )
                  }
                />

                <span className="premium-checkbox" />

                <span>
                  Keep me signed in
                </span>

              </label>

            </div>

            {/* LOGIN BUTTON */}
            <button
              type="submit"
              className="premium-login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span>
                    Signing in
                  </span>

                  <span className="premium-spinner" />
                </>
              ) : (
                <>
                  <span>
                    Sign in
                  </span>

                  <ArrowIcon />
                </>
              )}
            </button>

            {/* DIVIDER */}
            <div className="premium-divider">
              <span />

              <small>
                OR CONTINUE WITH
              </small>

              <span />
            </div>

            {/* GOOGLE LOGIN */}
            <button
              type="button"
              className="google-premium-button"
              disabled={loading}
              onClick={handleGoogleLogin}
            >
              <GoogleIcon />

              <span>
                Continue with Google
              </span>
            </button>

          </form>

          {/* SECURITY MESSAGE */}
          <div className="login-security">

            <ShieldIcon />

            <span>
              Secure & encrypted access
            </span>

          </div>

          {/* FOOTER */}
          <footer className="login-panel-footer">

            <div className="login-copyright">
              © {new Date().getFullYear()}{" "}
              {schoolName}
            </div>

            <div className="login-footer-links">

              <button
                type="button"
                onClick={() =>
                  navigate("/privacy")
                }
              >
                Privacy
              </button>

              <span>•</span>

              <button
                type="button"
                onClick={() =>
                  navigate("/terms")
                }
              >
                Terms
              </button>

            </div>

          </footer>

        </div>

      </section>

    </main>
  );
};

export default Login;